import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import CountUp from 'react-countup';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend 
} from 'recharts';
import { 
  Send, Users, CheckCircle, XCircle, Activity, Calendar, Clock, BarChart3, 
  ArrowUpRight, MailOpen, Mail, ArrowLeft, MousePointerClick, Smartphone, 
  Monitor, Tablet, Globe, Search, Download, RefreshCw, MoreVertical, ShieldAlert,
  MapPin, UserX, AlertTriangle, PlayCircle
} from 'lucide-react';
import { format, subDays, isAfter, isBefore, differenceInDays, addDays, isSameMonth, formatDistanceToNow } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthState } from "react-firebase-hooks/auth";
import auth from '../../../../firebase.init';
import { Link } from 'react-router-dom';


const PIE_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444'];

const EmailAnalyticsDashboard = () => {
  const [user] = useAuthState(auth);
  const [data, setData] = useState({ allLogs: [], totalUsers: 0, trackingEventsMap: {} });
  const [loading, setLoading] = useState(true);
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);
  
  const [chartRange, setChartRange] = useState(30); // number or 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'not_opened', 'clicked'

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!user?.email) return;
      try {
        const [userTemplatesRes, usersRes, trackingRes] = await Promise.all([
          axios.get(`https://emailserverjuly.shahlimon.online/user-email-templates?ownerEmail=${user.email}`).catch(() => ({ data: [] })),
          axios.get(`https://emailserverjuly.shahlimon.online/users?ownerEmail=${user.email}`).catch(() => ({ data: [] })),
          axios.get(`https://emailserverjuly.shahlimon.online/api/analytics/detailed-logs?ownerEmail=${user.email}`).catch(() => ({ data: { trackingEvents: {} } }))
        ]);

        const manualUsers = Array.isArray(userTemplatesRes.data) ? userTemplatesRes.data : [];
        const registeredUsers = Array.isArray(usersRes.data) ? usersRes.data : [];
        const allUsers = [...manualUsers, ...registeredUsers];
        
        let logs = [];
        allUsers.forEach(u => {
          if (u.messageLogs && Array.isArray(u.messageLogs)) {
            logs = [...logs, ...u.messageLogs];
          }
        });
        logs.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        const trackingEventsMap = trackingRes.data?.trackingEvents || {};

        setData({ allLogs: logs, totalUsers: allUsers.length, trackingEventsMap });
      } catch (error) {
        console.error("Error fetching analytics data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      fetchAnalyticsData();
    }
  }, [user]);

  const enhancedLogs = useMemo(() => {
    return data.allLogs.map((log) => {
      const trackingId = log.trackingId;
      const events = trackingId && data.trackingEventsMap[trackingId] ? data.trackingEventsMap[trackingId] : [];
      
      let isDelivered = true;
      let isOpened = false;
      let openCount = 0;
      let isClicked = false;
      let clickCount = 0;
      let isBounced = false;
      let isSpam = false;
      let isUnsubscribed = false;

      let device = null;
      let browser = null;
      let location = null;
      let ip = null;

      const timeline = [];
      const sentTime = new Date(log.date || Date.now());
      timeline.push({ type: 'sent', time: sentTime, desc: 'Email sent', icon: Send, color: 'text-secondary' });
      
      events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).forEach(ev => {
        if (ev.eventType === 'opened') {
           isOpened = true;
           openCount++;
           const ua = ev.metadata?.userAgent || "";
           if (!device) device = ua.includes('Mobile') || ua.includes('Android') || ua.includes('iPhone') ? 'Mobile' : 'Desktop';
           if (!browser) browser = ua.includes('Chrome') ? 'Chrome' : ua.includes('Safari') ? 'Safari' : ua.includes('Firefox') ? 'Firefox' : 'Other';
           if (!ip && ev.metadata?.ip) ip = ev.metadata.ip;
           timeline.push({ type: 'opened', time: new Date(ev.timestamp), desc: `Opened from ${ip || 'Unknown IP'}`, icon: MailOpen, color: 'text-info' });
        }
        if (ev.eventType === 'clicked') {
           isClicked = true;
           clickCount++;
           timeline.push({ type: 'clicked', time: new Date(ev.timestamp), desc: `Clicked ${ev.metadata?.url}`, icon: MousePointerClick, color: 'text-primary' });
        }
      });

      if (isClicked) {
        isOpened = true; // A click mathematically implies an open
        if (openCount === 0) openCount = 1;
      }

      return {
        ...log,
        tracking: {
          isDelivered, isOpened, openCount, isClicked, clickCount, isBounced, isSpam, isUnsubscribed,
          device, browser, location, ip, timeline
        }
      };
    });
  }, [data.allLogs, data.trackingEventsMap]);

  const stats = useMemo(() => {
    let totalSent = enhancedLogs.length;
    let delivered = 0, totalOpens = 0, uniqueOpens = 0, totalClicks = 0, uniqueClicks = 0;
    let bounced = 0, unsubscribed = 0, spam = 0;

    const deviceMap = {};
    const browserMap = {};

    enhancedLogs.forEach(log => {
      const t = log.tracking;
      if (t.isDelivered) delivered++;
      if (t.isBounced) bounced++;
      if (t.isOpened) uniqueOpens++;
      if (t.isClicked) uniqueClicks++;
      if (t.isUnsubscribed) unsubscribed++;
      if (t.isSpam) spam++;
      totalOpens += t.openCount;
      totalClicks += t.clickCount;

      if (t.device) deviceMap[t.device] = (deviceMap[t.device] || 0) + 1;
      if (t.browser) browserMap[t.browser] = (browserMap[t.browser] || 0) + 1;
    });

    const openRate = delivered ? ((uniqueOpens / delivered) * 100).toFixed(1) : 0;
    const clickRate = delivered ? ((uniqueClicks / delivered) * 100).toFixed(1) : 0;
    const ctor = uniqueOpens ? ((uniqueClicks / uniqueOpens) * 100).toFixed(1) : 0;
    const bounceRate = totalSent ? ((bounced / totalSent) * 100).toFixed(1) : 0;

    // Charts data
    const now = new Date();
    let rangeAgo, daysCount;
    let customStart = null, customEnd = null;

    if (chartRange === 'custom' && startDate && endDate) {
      customStart = new Date(startDate);
      customEnd = new Date(endDate);
      customEnd.setHours(23, 59, 59, 999); // Include the whole end day
      daysCount = Math.max(1, differenceInDays(customEnd, customStart) + 1);
      rangeAgo = customStart;
    } else {
      daysCount = Number(chartRange) || 30;
      rangeAgo = subDays(now, daysCount);
    }

    const chartDataMap = {};
    for (let i = 0; i < daysCount; i++) {
      const d = chartRange === 'custom' && startDate ? addDays(customStart, i) : subDays(now, daysCount - 1 - i);
      chartDataMap[format(d, 'MMM dd')] = { name: format(d, 'MMM dd'), sent: 0, opens: 0, clicks: 0 };
    }
    
    enhancedLogs.forEach(log => {
      if (log.date) {
        const logDate = new Date(log.date);
        
        let inRange = false;
        if (chartRange === 'custom' && customStart && customEnd) {
           inRange = logDate >= customStart && logDate <= customEnd;
        } else {
           inRange = isAfter(logDate, rangeAgo);
        }

        if (inRange) {
          const key = format(logDate, 'MMM dd');
          if (chartDataMap[key]) {
            chartDataMap[key].sent++;
            if (log.tracking.isOpened) chartDataMap[key].opens++;
            if (log.tracking.isClicked) chartDataMap[key].clicks++;
          }
        }
      }
    });

    const deviceData = Object.entries(deviceMap).map(([name, value]) => ({ name, value }));
    const browserData = Object.entries(browserMap).map(([name, value]) => ({ name, value }));

    // Tables data
    const filteredActivity = enhancedLogs.filter(log => {
      const matchesSearch = log.recipientEmail?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            log.subject?.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;

      // Date filtering
      if (log.date) {
        const logDate = new Date(log.date);
        if (chartRange === 'custom' && customStart && customEnd) {
           if (logDate < customStart || logDate > customEnd) return false;
        } else {
           if (!isAfter(logDate, rangeAgo)) return false;
        }
      }

      if (activeTab === 'not_opened') return log.tracking.isDelivered && !log.tracking.isOpened;
      if (activeTab === 'clicked') return log.tracking.isClicked;
      return true; // 'all'
    });

    return {
      totalSent, delivered, totalOpens, uniqueOpens, totalClicks, uniqueClicks, 
      openRate, clickRate, ctor, bounceRate, unsubscribed, spam,
      totalContacts: data.totalUsers,
      chartData: Object.values(chartDataMap),
      deviceData, browserData,
      filteredActivity
    };
  }, [enhancedLogs, chartRange, startDate, endDate, searchQuery, activeTab]);

  const exportToCSV = () => {
    if (!stats.filteredActivity || stats.filteredActivity.length === 0) return;

    const headers = ["Recipient Name", "Recipient Email", "Subject", "Date", "Status", "Opens", "Clicks", "Device", "Browser", "IP"];
    
    const csvContent = [
      headers.join(","),
      ...stats.filteredActivity.map(log => {
        const t = log.tracking;
        const status = t.isClicked ? 'Clicked' : t.isOpened ? 'Opened' : t.isDelivered ? 'Delivered' : 'Sent';
        return [
          `"${log.recipientName || ''}"`,
          `"${log.recipientEmail || ''}"`,
          `"${log.subject || log.templateName || ''}"`,
          `"${log.date ? new Date(log.date).toLocaleString() : ''}"`,
          `"${status}"`,
          `"${t.openCount}"`,
          `"${t.clickCount}"`,
          `"${t.device || ''}"`,
          `"${t.browser || ''}"`,
          `"${t.ip || ''}"`
        ].join(",");
      })
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `email_analytics_export_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const StatCard = ({ title, value, subtext, icon: Icon, color }) => (
    <div className="enterprise-card p-4 h-100">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <p className="text-muted fw-semibold mb-1 text-uppercase tracking-wider" style={{ fontSize: '0.75rem' }}>{title}</p>
          <h3 className="fw-bolder text-white mb-0 d-flex align-items-center gap-2">
            {typeof value === 'number' ? <CountUp end={value} separator="," duration={2} /> : value}
          </h3>
        </div>
        <div className={`p-2 rounded-lg bg-${color} bg-opacity-10 text-${color}`}>
          <Icon size={20} strokeWidth={2.5} />
        </div>
      </div>
      {subtext && <div className="text-gray-400" style={{ fontSize: '0.8rem' }}>{subtext}</div>}
    </div>
  );

  if (loading && enhancedLogs.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-dark-main">
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }} />
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-dark-main py-4 px-3 px-lg-4">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <Link to="/admin/email-marketing" className="btn btn-sm btn-link text-muted p-0 text-decoration-none hover-text-white transition-all">
              <ArrowLeft size={16} className="me-1" /> Dashboard
            </Link>
          </div>
          <h1 className="fw-bold text-white fs-3 mb-0 d-flex align-items-center gap-2">
            <BarChart3 className="text-primary" /> Enterprise Analytics
          </h1>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {chartRange === 'custom' && (
            <div className="d-flex gap-2">
              <input 
                type="date" 
                className="form-control bg-dark text-white border-gray-800 shadow-none" 
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span className="text-gray-500 d-flex align-items-center">to</span>
              <input 
                type="date" 
                className="form-control bg-dark text-white border-gray-800 shadow-none" 
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          )}
          <select 
            className="form-select bg-dark text-white border-gray-800 shadow-none"
            value={chartRange}
            onChange={(e) => setChartRange(e.target.value === 'custom' ? 'custom' : Number(e.target.value))}
            style={{ width: '140px' }}
          >
            <option value={7}>Last 7 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value={90}>Last 90 Days</option>
            <option value="custom">Custom Range</option>
          </select>
          <button 
            className="btn btn-dark border border-gray-800 d-flex align-items-center gap-2 px-3 flex-shrink-0"
            onClick={exportToCSV}
            disabled={stats.filteredActivity.length === 0}
          >
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {/* Primary KPIs */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-xl-2"><StatCard title="Total Contacts" value={stats.totalContacts} subtext="In your audience" color="secondary" icon={Users} /></div>
        <div className="col-6 col-md-4 col-xl-2"><StatCard title="Sent" value={stats.totalSent} subtext={`Total ${stats.totalSent} Emails Sent`} color="primary" icon={Send} /></div>
        <div className="col-6 col-md-4 col-xl-2"><StatCard title="Delivered" value={stats.delivered} subtext={`${((stats.delivered/stats.totalSent)*100 || 0).toFixed(1)}% Delivery Rate`} color="success" icon={CheckCircle} /></div>
        <div className="col-6 col-md-4 col-xl-2"><StatCard title="Open Rate" value={`${stats.openRate}%`} subtext={`${stats.uniqueOpens} Unique Opens`} color="info" icon={MailOpen} /></div>
        <div className="col-6 col-md-4 col-xl-2"><StatCard title="Click Rate" value={`${stats.clickRate}%`} subtext={`${stats.uniqueClicks} Unique Clicks`} color="warning" icon={MousePointerClick} /></div>
        <div className="col-6 col-md-4 col-xl-2"><StatCard title="CTOR" value={`${stats.ctor}%`} subtext="Click-to-Open Rate" color="purple" icon={Activity} /></div>
      </div>

      {/* Charts Row */}
      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="enterprise-card p-4 h-100">
            <h6 className="fw-bold text-white mb-4">Engagement Trends</h6>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/></linearGradient>
                    <linearGradient id="colorOpens" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
                  <RechartsTooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px' }} itemStyle={{ fontWeight: 500 }} />
                  <Area type="monotone" dataKey="sent" stroke="#3b82f6" fillOpacity={1} fill="url(#colorSent)" strokeWidth={2} name="Sent" />
                  <Area type="monotone" dataKey="opens" stroke="#10b981" fillOpacity={1} fill="url(#colorOpens)" strokeWidth={2} name="Opens" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table Section */}
      <div className="enterprise-card overflow-hidden">
        <div className="border-bottom border-gray-800 bg-dark-surface p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <div className="d-flex gap-2">
            {['all', 'not_opened', 'clicked'].map(tab => (
              <button 
                key={tab} 
                onClick={() => setActiveTab(tab)}
                className={`btn btn-sm px-3 rounded-pill transition-all ${activeTab === tab ? 'bg-primary text-white border-primary' : 'btn-dark text-gray-400 border-gray-800'}`}
              >
                {tab === 'all' ? 'All Activity' : tab === 'not_opened' ? 'Not Opened' : 'Clicked Links'}
              </button>
            ))}
          </div>
          <div className="position-relative" style={{ width: '250px' }}>
            <Search size={16} className="position-absolute text-gray-500" style={{ top: '10px', left: '12px' }} />
            <input 
              type="text" 
              className="form-control form-control-sm bg-dark text-white border-gray-800 shadow-none ps-5 rounded-pill"
              placeholder="Search recipient or subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <div className="table-responsive">
          <table className="table table-dark table-hover mb-0 align-middle">
            <thead className="bg-dark-surface">
              <tr>
                <th className="text-gray-500 fw-medium small py-3 px-4 border-gray-800">RECIPIENT</th>
                <th className="text-gray-500 fw-medium small py-3 px-4 border-gray-800">CAMPAIGN / SUBJECT</th>
                <th className="text-gray-500 fw-medium small py-3 px-4 border-gray-800">STATUS</th>
                <th className="text-gray-500 fw-medium small py-3 px-4 border-gray-800">LAST ACTIVITY</th>
                <th className="text-gray-500 fw-medium small py-3 px-4 border-gray-800 text-end">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {stats.filteredActivity.slice(0, 50).map((log, idx) => (
                <tr key={idx} className="border-gray-800 cursor-pointer" onClick={() => setSelectedLogDetail(log)}>
                  <td className="px-4 py-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle bg-gray-800 d-flex align-items-center justify-content-center fw-bold text-gray-300" style={{ width: 36, height: 36 }}>
                        {(log.recipientName || log.recipientEmail || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="fw-medium text-white">{log.recipientName || 'Unknown Contact'}</div>
                        <div className="text-gray-500 small">{log.recipientEmail}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white text-truncate" style={{ maxWidth: '250px' }}>{log.subject || log.templateName}</div>
                    <div className="text-gray-500 small d-flex align-items-center gap-1">
                      <Calendar size={12} /> {log.date ? formatDistanceToNow(new Date(log.date), { addSuffix: true }) : ''}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {log.tracking.isClicked ? <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 rounded-pill px-2 py-1"><MousePointerClick size={12} className="me-1"/> Clicked</span> :
                     log.tracking.isOpened ? <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill px-2 py-1"><MailOpen size={12} className="me-1"/> Opened</span> :
                     log.tracking.isDelivered ? <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill px-2 py-1"><CheckCircle size={12} className="me-1"/> Delivered</span> :
                     log.tracking.isBounced ? <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 rounded-pill px-2 py-1"><XCircle size={12} className="me-1"/> Bounced</span> :
                     <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 rounded-pill px-2 py-1">Sent</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-gray-300 small">
                      {log.tracking.timeline.length > 0 ? log.tracking.timeline[log.tracking.timeline.length - 1].desc : 'No activity'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-end" onClick={e => e.stopPropagation()}>
                    <button className="btn btn-sm btn-link text-gray-400 hover-text-white p-1"><MoreVertical size={16} /></button>
                  </td>
                </tr>
              ))}
              {stats.filteredActivity.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-gray-500">
                    <Search size={32} className="mb-3 opacity-50 mx-auto" />
                    No logs found matching your criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recipient Detail Modal */}
      <AnimatePresence>
        {selectedLogDetail && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="modal-backdrop-custom d-flex align-items-center justify-content-center"
            onClick={() => setSelectedLogDetail(null)}
          >
            <motion.div
              initial={{ y: 50, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.95 }}
              transition={{ type: "spring", bounce: 0, duration: 0.4 }}
              className="modal-content-custom enterprise-card w-100 mx-3 mx-md-auto d-flex flex-column overflow-hidden"
              style={{ maxWidth: '1200px', maxHeight: '90vh' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="border-bottom border-gray-800 p-4 d-flex justify-content-between align-items-center bg-dark-surface">
                <div className="d-flex align-items-center gap-3">
                  <div className="rounded-circle bg-primary bg-opacity-20 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                    <Users size={24} className="text-primary" />
                  </div>
                  <div>
                    <h5 className="mb-1 text-white fw-bold">{selectedLogDetail.recipientName || 'Contact Details'}</h5>
                    <div className="text-gray-400 small d-flex align-items-center gap-2">
                      <Mail size={14} /> {selectedLogDetail.recipientEmail} 
                      {selectedLogDetail.tracking.location && <><Globe size={14} className="ms-2" /> {selectedLogDetail.tracking.location}</>}
                    </div>
                  </div>
                </div>
                <button className="btn-close btn-close-white" onClick={() => setSelectedLogDetail(null)}></button>
              </div>

              <div className="row g-0 flex-grow-1 overflow-hidden">
                {/* Timeline Column */}
                <div className="col-md-5 border-end border-gray-800 bg-dark-main p-4 overflow-y-auto custom-scrollbar" style={{ maxHeight: 'calc(90vh - 89px)' }}>
                  <h6 className="text-white fw-bold mb-4 d-flex align-items-center gap-2">
                    <Activity size={18} className="text-info" /> Activity Timeline
                  </h6>
                  <div className="timeline-container ps-2 border-start border-gray-800 ms-2">
                    {selectedLogDetail.tracking.timeline.map((event, idx) => {
                      const Icon = event.icon;
                      return (
                        <div key={idx} className="position-relative mb-4 ps-4">
                          <div className={`position-absolute bg-dark-main border border-gray-800 rounded-circle d-flex align-items-center justify-content-center`} style={{ width: 28, height: 28, left: -14, top: -4 }}>
                            <Icon size={14} className={event.color} />
                          </div>
                          <div>
                            <div className="text-white fw-medium mb-1 text-capitalize">{event.type}</div>
                            <div className="text-gray-400 small mb-1">{event.desc}</div>
                            <div className="text-gray-500" style={{ fontSize: '0.7rem' }}>{format(event.time, 'MMM dd, yyyy - hh:mm a')}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Details Column */}
                <div className="col-md-7 p-4 bg-dark-surface overflow-y-auto custom-scrollbar" style={{ maxHeight: 'calc(90vh - 89px)' }}>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 className="text-white fw-bold mb-0">Engagement Profile</h6>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-light d-flex align-items-center gap-2"><PlayCircle size={14} /> Automation</button>
                      <button className="btn btn-sm btn-primary d-flex align-items-center gap-2"><Send size={14} /> Resend</button>
                    </div>
                  </div>



                  <h6 className="text-gray-400 fw-semibold small text-uppercase tracking-wider mb-3">Message Content</h6>
                  <div className="bg-dark p-4 rounded-3 border border-gray-800 mb-3">
                    <div className="fw-bold text-white mb-2 pb-2 border-bottom border-gray-800">Subject: {selectedLogDetail.subject || '(No Subject)'}</div>
                    {selectedLogDetail.body ? (
                      <div className="bg-white rounded p-3 text-dark mt-3 overflow-y-auto overflow-x-hidden custom-scrollbar email-preview-container" style={{ maxHeight: '250px' }} dangerouslySetInnerHTML={{ __html: `<style>
                        .email-preview-container { word-wrap: break-word !important; overflow-wrap: break-word !important; word-break: break-word !important; }
                        .email-preview-container * { max-width: 100% !important; box-sizing: border-box !important; }
                        .email-preview-container img { height: auto !important; }
                      </style>` + selectedLogDetail.body }} />
                    ) : (
                      <div className="text-gray-500 text-center py-4">Email body content not available</div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx="true">{`
        .bg-dark-main { background-color: #020617; }
        .bg-dark-surface { background-color: #0f172a; }
        .bg-dark { background-color: #1e293b !important; }
        .text-gray-300 { color: #cbd5e1 !important; }
        .text-gray-400 { color: #94a3b8 !important; }
        .text-gray-500 { color: #64748b !important; }
        .border-gray-800 { border-color: #1e293b !important; }
        .tracking-wider { letter-spacing: 0.05em; }
        
        .enterprise-card {
          background-color: #0f172a;
          border: 1px solid #1e293b;
          border-radius: 1rem;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06);
        }

        .cursor-pointer { cursor: pointer; }
        .hover-text-white:hover { color: #fff !important; }
        .transition-all { transition: all 0.2s ease-in-out; }

        .modal-backdrop-custom {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(2, 6, 23, 0.8);
          backdrop-filter: blur(4px);
          z-index: 1050;
        }

        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb { background: #475569; }

        .table-hover tbody tr:hover {
          background-color: rgba(255, 255, 255, 0.02) !important;
        }
      `}</style>
    </div>
  );
};

export default EmailAnalyticsDashboard;
